import React from "react";
import { BASE_URL } from "@/constants";
import { buildAssetUrl } from "@/utils/mapMenuItemToDishCardProps";
import type { TAllergen, TMarkers } from "@/types";
import { Image } from "react-bootstrap";

type TRenderDishMarkersVariant = "icons" | "list";

interface RenderDishMarkersProps {
  markers?: TMarkers[];
  allergens?: TAllergen[];
  sizePx?: number;
  className?: string;
  variant?: TRenderDishMarkersVariant;
  showLabel?: boolean;
  allergenFallbackIcon?: React.ReactNode;
}

const RenderDishMarkers: React.FC<RenderDishMarkersProps> = ({
  markers = [],
  allergens = [],
  sizePx = 16,
  className = "",
  variant = "icons",
  showLabel = variant === "list",
}) => {
  const hasAnyBadges = markers.length > 0 || allergens.length > 0;
  if (!hasAnyBadges) return null;

  const isList = variant === "list";

  const wrapperClassName = isList
    ? `d-flex flex-column gap-2 ${className}`
    : `d-flex flex-wrap gap-2 align-items-center ${className}`;

  const iconWrapperClassName = isList
    ? "d-inline-flex align-items-center justify-content-center flex-shrink-0"
    : "d-inline-flex align-items-center justify-content-center rounded-circle";

  const renderMarkerIcon = (marker: TMarkers) => {
    const markerIconUrl = buildAssetUrl(BASE_URL, marker.favicon ?? null);

    if (!markerIconUrl) return null;
    return (
      <Image
        src={markerIconUrl}
        alt=""
        className="w-100 h-100 object-fit-contain"
        loading="lazy"
      />
    );
  };

  const renderAllergenIcon = (allergen: TAllergen) => {
    const allergenIconUrl = buildAssetUrl(BASE_URL, allergen.favicon ?? null);

    if (!allergenIconUrl) return null;
    return (
      <Image
        src={allergenIconUrl}
        alt=""
        className="w-100 h-100 object-fit-contain"
        loading="lazy"
      />
    );
  };

  return (
    <div className={wrapperClassName}>
      {markers.map((marker) => {
        const markerName = marker.name ?? "";
        if (isList) {
          return (
            <div
              key={`m-${marker.id}`}
              className="d-flex align-items-center gap-2"
            >
              <span
                className={iconWrapperClassName}
                style={{ width: sizePx, height: sizePx }}
                title={markerName}
              >
                {renderMarkerIcon(marker)}
              </span>

              {showLabel ? (
                <span className="fs-14-16">{markerName}</span>
              ) : null}
            </div>
          );
        }

        return (
          <span
            key={`m-${marker.id}`}
            className={iconWrapperClassName}
            style={{ width: sizePx, height: sizePx }}
            title={markerName}
          >
            {renderMarkerIcon(marker)}
          </span>
        );
      })}

      {allergens.map((allergen) => {
        const allergenName = allergen.name ?? "";

        if (isList) {
          return (
            <div
              key={`a-${allergen.id}`}
              className="d-flex align-items-center gap-2"
            >
              <span
                style={{ width: sizePx, height: sizePx }}
                className={iconWrapperClassName}
                title={allergenName}
              >
                {renderAllergenIcon(allergen)}
              </span>

              {showLabel ? (
                <span className="fs-14-16">{allergenName}</span>
              ) : null}
            </div>
          );
        }

        return (
          <span
            key={`a-${allergen.id}`}
            className={iconWrapperClassName}
            style={{ width: sizePx, height: sizePx }}
            title={allergenName}
          >
            {renderAllergenIcon(allergen)}
          </span>
        );
      })}
    </div>
  );
};

export default React.memo(RenderDishMarkers);
